import type React from "react"

import { Box } from "@mui/material"
import { visuallyHidden } from "@mui/utils"

/**
 * The numbers behind a chart, as a table only assistive tech reads.
 *
 * These charts draw a whole series as one SVG path, so every individual value
 * is invisible to a screen reader and unreachable by keyboard. A summarising
 * `aria-label` on the svg says what the chart is ABOUT, never what it shows,
 * which leaves the actual readout — 168 hourly values on the week chart — with
 * no non-visual route at all.
 *
 * A hidden table is the fix rather than per-point focus targets: making every
 * point focusable would add 168 tab stops to the page for a chart a sighted
 * user reads at a glance. A table is navigable with the screen reader's own
 * table commands and changes nothing visually.
 */
export const ChartDataTable: React.FC<{
  caption: string
  columns: string[]
  /** One row per point. The first cell is the row header (the x value). */
  rows: (string | number)[][]
}> = ({ caption, columns, rows }) => (
  <Box component="table" sx={visuallyHidden}>
    <caption>{caption}</caption>
    <thead>
      <tr>
        {columns.map((c) => (
          <th key={c} scope="col">
            {c}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={String(row[0])}>
          {row.map((cell, i) =>
            i === 0 ? (
              <th key={columns[i]} scope="row">
                {cell}
              </th>
            ) : (
              <td key={columns[i]}>{cell}</td>
            )
          )}
        </tr>
      ))}
    </tbody>
  </Box>
)
